/** @jsxImportSource @emotion/react */

import { memo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  Dialog,
  DialogContent,
  Grid,
  Card,
  CardHeader,
  CardContent,
  TextareaAutosize,
  TextField,
  Typography,
  InputAdornment,
  FormControl,
  FormLabel,
} from "@material-ui/core";

import { Label, Button, ViewState, Icon } from "..";
import config from "../../config";
import { useAccount } from "../../context/account";
import { useToast } from "../../context/toast";
import useAuthSWR, { authFetcher } from "../../hooks/useAuthSWR";
import useCopy from "../../hooks/useCopy";
import useDownload from "../../hooks/useDownload";
import { Tier } from "../../types";

import {
  dialog,
  cardHeader,
  cardContent,
  labelBlock,
  codeBlock,
  redeemCodeCard,
  section,
  inputLabel,
  redeemCodeLabel,
  redeemCodeInput,
  redeemCodeSection,
  redeemCodeList,
  buttonSection,
  buttonBorderSection,
  button,
  disabled,
  disabledLast,
  guide,
  exclamationIcon,
} from "./styles";

export interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
  isPublicEvent: boolean;
  hasChatroom: boolean;
}

const API_PATH = "/codes/live-events";
const MAX_LIMIT = 50;
const MAX_REDEEM_CODE = {
  [Tier.FREE_TRIAL]: 30,
  [Tier.OFFICIAL]: 250,
  [Tier.ENTERPRISE]: 1000,
};

const EventSiteModal = ({ open, onClose, eventId, isPublicEvent, hasChatroom }: Props): JSX.Element => {
  const intl = useIntl();
  const eventSiteLink = useCopy();
  const redeemCodeLink = useCopy();
  const { download } = useDownload();
  const showToast = useToast();
  const { tier } = useAccount();
  const [loading, setLoading] = useState(false);

  const fetchUrl = `${API_PATH}/${eventId}?limit=${MAX_LIMIT}`;
  const { data, state, mutate } = useAuthSWR(open && !isPublicEvent ? fetchUrl : null, {
    revalidateOnFocus: false,
    dedupingInterval: 0, // [MB-190] default dedupingInterval 2000ms was too long and might get stale value from cache.
  });

  const { register, watch, setValue, errors, clearErrors, trigger } = useForm();
  const redeemCodeAmount = watch("redeemCodeAmount", "");

  useEffect(() => {
    if (redeemCodeAmount.length > 0) {
      clearErrors();
    }
  }, [clearErrors, redeemCodeAmount]);

  const handleCloseModal = () => {
    onClose();
  };

  const handleDownloadCSV = async (): Promise<void> => {
    try {
      const csvData = await authFetcher(`/codes/live-events/${eventId}/file`, {
        responseType: "blob",
      });
      const blob = new Blob([csvData as BlobPart], {
        type: "text/csv;charset=utf-8;",
      });
      // eslint-disable-next-line compat/compat
      const url = URL.createObjectURL(blob);
      await download(url, `Redeem_Codes_${eventId}.csv`);
    } catch (error) {
      console.error("> Event site > handleDownloadCSV ", error);
      showToast(intl.formatMessage({ id: "general.error" }), {
        type: "error",
      });
    }
  };

  const getEventSiteLink = () => {
    // eslint-disable-next-line compat/compat
    const url = new URL(config.EVENT_SITE_URL);
    if (isPublicEvent) {
      url.searchParams.append("event", eventId);

      if (hasChatroom) {
        url.searchParams.append("chat", Number(hasChatroom).toString());
      }
    }
    return url.toString();
  };

  const totalRedeemCodes = data?.paging?.total;
  const remainRedeemCodes =
    tier !== Tier.ENTERPRISE && totalRedeemCodes ? MAX_REDEEM_CODE[tier] - totalRedeemCodes : MAX_REDEEM_CODE[tier];
  const isError = Boolean(errors?.redeemCodeAmount);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} css={dialog} onExited={handleCloseModal} disableEnforceFocus>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={intl.formatMessage({
                  id: "event-site.title",
                })}
                css={cardHeader}
              />
              <CardContent css={cardContent}>
                <Grid container>
                  <Grid item xs={12} css={labelBlock}>
                    <Label
                      text={intl.formatMessage({
                        id: "event-site.sharing-link.label",
                      })}
                    />
                    <Button color="primary" size="small" onClick={(): void => eventSiteLink.copy()}>
                      {intl.formatMessage({ id: "btn.copy" })}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <TextareaAutosize css={codeBlock} readOnly value={getEventSiteLink()} ref={eventSiteLink.ref} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      color="primary"
                      size="small"
                      onClick={(): void => {
                        window.open(getEventSiteLink(), "_blank");
                      }}
                    >
                      {intl.formatMessage({
                        id: "event-site.open-in-new-window",
                      })}
                    </Button>
                  </Grid>
                  {isPublicEvent && (
                    <Grid item xs={12} css={buttonSection}>
                      <Button variant="contained" color="primary" css={button} onClick={handleCloseModal}>
                        {intl.formatMessage({
                          id: "btn.done",
                        })}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          {!isPublicEvent && (
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title={intl.formatMessage({
                    id: "event-site.create-redeem-codes.title",
                  })}
                  css={cardHeader}
                />
                <CardContent css={redeemCodeCard}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} css={section}>
                      {/** To align design */}
                      <FormControl fullWidth error={isError}>
                        <FormLabel css={inputLabel}>
                          {intl.formatMessage({
                            id: "event-site.create-redeem-codes.label",
                          })}
                          {tier !== Tier.ENTERPRISE &&
                            intl.formatMessage(
                              {
                                id: "event-site.create-redeem-codes.maximum-and-remaining",
                              },
                              {
                                max: MAX_REDEEM_CODE[tier],
                                remaining: remainRedeemCodes,
                              }
                            )}
                        </FormLabel>
                        <TextField
                          type="number"
                          css={redeemCodeInput}
                          name="redeemCodeAmount"
                          placeholder={intl.formatMessage({
                            id: "event-site.create-redeem-codes.placeholder",
                          })}
                          defaultValue=""
                          inputProps={{
                            min: 0,
                            max: remainRedeemCodes,
                          }}
                          inputRef={register({
                            max: {
                              value: remainRedeemCodes,
                              message:
                                tier === Tier.ENTERPRISE
                                  ? intl.formatMessage(
                                      {
                                        id: "event-site.create-redeem-codes.error",
                                      },
                                      { max: remainRedeemCodes }
                                    )
                                  : intl.formatMessage({
                                      id: "event-site.create-redeem-codes.general-error",
                                    }),
                            },
                            min: {
                              value: 1,
                              message: intl.formatMessage({
                                id: "event-site.create-redeem-codes.error.invalid-value",
                              }),
                            },
                          })}
                          error={isError}
                          helperText={errors?.redeemCodeAmount?.message}
                          // eslint-disable-next-line react/jsx-no-duplicate-props
                          {...(isError && {
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Icon name="exclamation" viewBox="0 0 48 48" css={exclamationIcon} />
                                </InputAdornment>
                              ),
                            },
                          })}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} css={redeemCodeSection}>
                      <ViewState state={state}>
                        {totalRedeemCodes > 0 && (
                          <>
                            <Grid item xs={12} css={section}>
                              <Typography component="span" variant="body2" css={redeemCodeLabel}>
                                {intl.formatMessage(
                                  {
                                    id: "event-site.create-redeem-codes.redeem-codes",
                                  },
                                  { count: data?.paging.total }
                                )}
                              </Typography>
                              <Button color="primary" size="small" icon="import" onClick={handleDownloadCSV}>
                                {intl.formatMessage({
                                  id: "btn.download-csv",
                                })}
                              </Button>
                            </Grid>
                            <Grid item xs={12} css={redeemCodeList}>
                              {/* eslint-disable react/no-array-index-key */}
                              {data?.data?.codes?.map((code: string, idx: number) => (
                                <TextField
                                  key={code}
                                  fullWidth
                                  name={`redeemCode-${idx}`}
                                  css={[disabled, idx === data?.data?.codes?.length - 1 && disabledLast]}
                                  inputProps={{
                                    readOnly: true,
                                  }}
                                  // eslint-disable-next-line react/jsx-no-duplicate-props
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="start">
                                        <Button
                                          color="primary"
                                          size="small"
                                          icon="copy"
                                          onClick={() => {
                                            redeemCodeLink.copy(`${config.EVENT_SITE_URL}?code=${code}`);
                                          }}
                                        >
                                          {intl.formatMessage({
                                            id: "btn.copy-link",
                                          })}
                                        </Button>
                                      </InputAdornment>
                                    ),
                                  }}
                                  defaultValue={code}
                                />
                              ))}
                              {data?.paging?.total >= MAX_LIMIT && (
                                <Button css={guide} fullWidth color="primary" onClick={handleDownloadCSV}>
                                  {intl.formatMessage({
                                    id: "event-site.download-csv-guide",
                                  })}
                                </Button>
                              )}
                            </Grid>
                          </>
                        )}
                      </ViewState>
                    </Grid>
                    <Grid item xs={12} css={[buttonSection, redeemCodeAmount && buttonBorderSection]}>
                      {redeemCodeAmount ? (
                        <>
                          <Button css={button} onClick={handleCloseModal}>
                            {intl.formatMessage({
                              id: "btn.cancel",
                            })}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            css={button}
                            disabled={loading}
                            loading={loading}
                            onClick={async () => {
                              const valid = await trigger();
                              if (!valid) return;
                              try {
                                setLoading(true);
                                await authFetcher(`/codes/live-events/${eventId}`, {
                                  method: "post",
                                  data: {
                                    amount: parseInt(redeemCodeAmount, 10),
                                  },
                                });
                                mutate();
                                handleDownloadCSV();
                              } catch (error) {
                                console.error("> Event site > CreateRedeemCodes", error);
                                showToast(intl.formatMessage({ id: "general.error" }), {
                                  type: "error",
                                });
                              } finally {
                                setValue("redeemCodeAmount", "");
                                setLoading(false);
                              }
                            }}
                          >
                            {intl.formatMessage({
                              id: "event-site.btn.create",
                            })}
                          </Button>
                        </>
                      ) : (
                        <Button variant="contained" color="primary" css={button} onClick={handleCloseModal}>
                          {intl.formatMessage({
                            id: "btn.done",
                          })}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default memo(EventSiteModal);
